/**
 * Imagery, terrain and tileset layers: offline NaturalEarthII base, Google or
 * Esri map styles, night lights, world terrain, OSM buildings, Google
 * Photorealistic 3D auto-load, weather WMS overlays and the aurora image
 * (loader-globe.js rows 3, 4, 7, 8, 52 to 54; loader-weather.js; loader-aurora.js).
 */
import * as Cesium from 'cesium';
import { WEATHER } from '../constants';
import type { MapStyle, WeatherImageryOptions } from './GlobeEngine';
import { ESRI_CREDIT, ESRI_TILE_ROOT, MAP_STYLES } from './mapStyles';
import { AURORA_CANVAS_HEIGHT, AURORA_CANVAS_WIDTH } from './auroraImage';
import { errorText, type GlobeContext } from './context';

export const OSM_BUILDINGS_ASSET_ID = 96188;
export const NIGHT_LIGHTS_ASSET_ID = 3812;
export const GOOGLE_3D_LOAD_BELOW_M = 50_000;
export const GOOGLE_3D_UNLOAD_ABOVE_M = 100_000;
/** After a failed Google 3D load, camera moves below 50 km retry at most this often. */
export const GOOGLE_3D_RETRY_MS = 30_000;
export const DEFAULT_WEATHER_OPACITY = 0.5;
/** Earth at Night opacity on the night side (it is fully transparent by day). */
export const NIGHT_LIGHTS_NIGHT_ALPHA = 0.9;
/** Lights brightness: the globe's day-night shading multiplies the night side by 0.3. */
export const NIGHT_LIGHTS_BRIGHTNESS = 2.0;

/** Verified raster services; do not depend on the undocumented Google tile endpoint. */
export function mapStyleProvider(style: MapStyle): Cesium.ImageryProvider {
  const config = MAP_STYLES.find((s) => s.id === style) ?? MAP_STYLES[0];
  return new Cesium.UrlTemplateImageryProvider({
    url: `${ESRI_TILE_ROOT}${config.service}/MapServer/tile/{z}/{y}/{x}`,
    maximumLevel: config.maxLevel,
    credit: ESRI_CREDIT,
  });
}

export class ImageryLayerManager {
  private base: Cesium.ImageryLayer | null = null;
  private labels: Cesium.ImageryLayer | null = null;
  private offline: Cesium.ImageryLayer | null = null;
  private mapStyle: MapStyle = 'satellite';
  private night: Cesium.ImageryLayer | null = null;
  private weather: Cesium.ImageryLayer | null = null;
  private aurora: Cesium.ImageryLayer | null = null;
  private weatherOpacity = DEFAULT_WEATHER_OPACITY;
  private weatherIdentity: string | null = null;
  private nightWanted = false;
  private terrainEnabled = false;
  private osm: Cesium.Cesium3DTileset | null = null;
  private osmLoading: Promise<Cesium.Cesium3DTileset> | null = null;
  private google: Cesium.Cesium3DTileset | null = null;
  private googleLoading = false;
  /** performance.now() of the last failed Google 3D load (null after a success or a re-enable). */
  private googleFailedAt: number | null = null;
  /** The current failure streak has been toasted; later failures in it only log. */
  private googleFailureReported = false;
  /** Auto-load of Google 3D under 50 km; on by default as in the original, gated by ionAssets. */
  private googleEnabled = true;
  private destroyed = false;

  constructor(private readonly ctx: GlobeContext) {}

  private ionAllowed(): boolean {
    return Boolean(this.ctx.options.ionAssets && this.ctx.options.ionToken);
  }

  /**
   * Offline NaturalEarthII at the bottom (async, from the Cesium assets copied
   * next to the bundle), under the chosen Esri raster style.
   */
  initBaseLayers(style: MapStyle): void {
    const layers = this.ctx.viewer.imageryLayers;
    this.setMapStyle(style);
    Cesium.TileMapServiceImageryProvider.fromUrl(Cesium.buildModuleUrl('Assets/Textures/NaturalEarthII'))
      .then((provider) => {
        if (this.destroyed) return;
        this.offline = layers.addImageryProvider(provider, 0);
        this.styleOfflineBase();
      })
      .catch((err: unknown) => {
        this.ctx.status('warn', `Offline basemap unavailable: ${errorText(err)}`);
      });
  }

  /**
   * Swap the base layer. The new layer goes directly above the offline basemap
   * (index 0 when it has not loaded yet), which keeps the offline layer at the
   * bottom instead of covering the new style as inserting at 0 would.
   */
  setMapStyle(style: MapStyle): void {
    this.mapStyle = style;
    this.styleOfflineBase();
    const layers = this.ctx.viewer.imageryLayers;
    if (this.base) {
      layers.remove(this.base, true);
      this.base = null;
    }
    if (this.labels) {
      layers.remove(this.labels, true);
      this.labels = null;
    }
    const index = this.offline ? layers.indexOf(this.offline) + 1 : 0;
    const provider = mapStyleProvider(style);
    let reported = false;
    provider.errorEvent.addEventListener(() => {
      if (reported || this.destroyed) return;
      reported = true;
      this.ctx.status(
        'warn',
        'Map tiles are unavailable in this area. The offline map remains underneath; try another map style.',
      );
    });
    this.base = layers.addImageryProvider(provider, index);
    if (style === 'satellite-labels') {
      this.labels = layers.addImageryProvider(
        new Cesium.UrlTemplateImageryProvider({
          url: `${ESRI_TILE_ROOT}Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`,
          maximumLevel: 19,
          credit: ESRI_CREDIT,
        }),
        index + 1,
      );
    }
    this.ctx.viewer.scene.requestRender();
  }

  /** Web Mercator leaves polar caps uncovered; keep the global fallback in the same palette. */
  private styleOfflineBase(): void {
    if (!this.offline) return;
    this.offline.saturation = this.mapStyle === 'dark' ? 0 : 1;
    this.offline.brightness = this.mapStyle === 'dark' ? 0.35 : 1;
  }

  isTerrainEnabled(): boolean {
    return this.terrainEnabled;
  }

  /** World terrain with vertex normals and water mask; resolves when the provider is ready. */
  async setTerrainEnabled(enabled: boolean): Promise<void> {
    const scene = this.ctx.viewer.scene;
    if (!enabled) {
      if (!this.terrainEnabled) return;
      scene.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      this.terrainEnabled = false;
      return;
    }
    if (this.terrainEnabled) return;
    if (!this.ctx.options.ionToken) throw new Error('World terrain needs a Cesium ion token');
    const terrain = Cesium.Terrain.fromWorldTerrain({ requestVertexNormals: true, requestWaterMask: true });
    this.terrainEnabled = true;
    scene.setTerrain(terrain);
    await new Promise<void>((resolve, reject) => {
      const offReady = terrain.readyEvent.addEventListener(() => {
        offReady();
        offError();
        resolve();
      });
      const offError = terrain.errorEvent.addEventListener((err: unknown) => {
        offReady();
        offError();
        this.terrainEnabled = false;
        scene.terrainProvider = new Cesium.EllipsoidTerrainProvider();
        reject(err instanceof Error ? err : new Error(`World terrain failed: ${errorText(err)}`));
      });
    });
  }

  /**
   * Earth at Night (ion asset 3812) on the night side only: transparent by day,
   * NIGHT_LIGHTS_NIGHT_ALPHA at night (darker land with the city lights), at
   * NIGHT_LIGHTS_BRIGHTNESS. The original drew it at alpha 0.35 over both sides.
   */
  async setNightLights(enabled: boolean): Promise<void> {
    this.nightWanted = enabled;
    const layers = this.ctx.viewer.imageryLayers;
    if (!enabled) {
      if (this.night) {
        layers.remove(this.night, true);
        this.night = null;
      }
      return;
    }
    if (this.night) return;
    if (!this.ionAllowed()) throw new Error('Night lights need ion assets enabled and an ion token');
    const provider = await Cesium.IonImageryProvider.fromAssetId(NIGHT_LIGHTS_ASSET_ID);
    if (this.destroyed || this.night || !this.nightWanted) return;
    this.night = layers.addImageryProvider(provider);
    this.night.dayAlpha = 0;
    this.night.nightAlpha = NIGHT_LIGHTS_NIGHT_ALPHA;
    this.night.brightness = NIGHT_LIGHTS_BRIGHTNESS;
  }

  /** Load OSM Buildings hidden so the toggle is instant later; failures go to the status channel. */
  preloadOsmBuildings(): void {
    if (!this.ionAllowed()) return;
    this.loadOsm()
      .then((tileset) => {
        tileset.show = false;
      })
      .catch((err: unknown) => {
        this.ctx.status('warn', `Could not load OSM Buildings: ${errorText(err)}`);
      });
  }

  private loadOsm(): Promise<Cesium.Cesium3DTileset> {
    if (this.osm) return Promise.resolve(this.osm);
    if (!this.osmLoading) {
      this.osmLoading = Cesium.Cesium3DTileset.fromIonAssetId(OSM_BUILDINGS_ASSET_ID)
        .then((tileset) => {
          if (this.destroyed) {
            tileset.destroy();
            throw new Error('Globe destroyed while OSM Buildings loaded');
          }
          this.osm = tileset;
          this.ctx.viewer.scene.primitives.add(tileset);
          return tileset;
        })
        .finally(() => {
          this.osmLoading = null;
        });
    }
    return this.osmLoading;
  }

  async setOsmBuildings(enabled: boolean): Promise<void> {
    if (!enabled) {
      if (this.osm) this.osm.show = false;
      return;
    }
    if (!this.ionAllowed()) throw new Error('OSM Buildings need ion assets enabled and an ion token');
    const tileset = await this.loadOsm();
    tileset.show = true;
  }

  /**
   * Google Photorealistic 3D auto-load; disabling unloads it, and OSM Buildings
   * hide while it is on. Turning it back on clears a failure streak, so the next
   * load is tried at once and a failure toasts again.
   */
  setGoogle3D(enabled: boolean): void {
    if (enabled && !this.googleEnabled) {
      this.googleFailedAt = null;
      this.googleFailureReported = false;
    }
    this.googleEnabled = enabled;
    if (!enabled && this.google) {
      this.ctx.viewer.scene.primitives.remove(this.google);
      this.google = null;
    }
    if (this.osm) this.osm.show = !enabled;
  }

  isGoogle3DEnabled(): boolean {
    return this.googleEnabled;
  }

  /**
   * Camera height hook: load under 50 km, unload above 100 km. A failed load is
   * toasted once per failure streak; while it lasts, camera moves retry at most
   * every GOOGLE_3D_RETRY_MS and later failures only log, until a load succeeds.
   */
  onCameraHeight(heightM: number): void {
    if (!this.googleEnabled || !this.ionAllowed()) return;
    if (heightM < GOOGLE_3D_LOAD_BELOW_M && !this.google && !this.googleLoading) {
      if (this.googleFailedAt !== null && performance.now() - this.googleFailedAt < GOOGLE_3D_RETRY_MS)
        return;
      this.googleLoading = true;
      Cesium.createGooglePhotorealistic3DTileset()
        .then((tileset) => {
          this.googleLoading = false;
          this.googleFailedAt = null;
          this.googleFailureReported = false;
          if (this.destroyed || !this.googleEnabled) {
            tileset.destroy();
            return;
          }
          this.google = tileset;
          this.ctx.viewer.scene.primitives.add(tileset);
        })
        .catch((err: unknown) => {
          this.googleLoading = false;
          this.googleFailedAt = performance.now();
          if (!this.googleFailureReported) {
            this.googleFailureReported = true;
            this.ctx.status('warn', `Google 3D tiles failed to load: ${errorText(err)}`);
          } else {
            console.warn(`[CesiumGlobe] Google 3D tiles failed to load again: ${errorText(err)}`);
          }
        });
    } else if (heightM > GOOGLE_3D_UNLOAD_ABOVE_M && this.google) {
      this.ctx.viewer.scene.primitives.remove(this.google);
      this.google = null;
    }
  }

  /**
   * Weather WMS overlay with an explicit advertised date, or the provider's latest image.
   * null removes the layer; an unknown key throws. Feature picking is off since
   * there is no info box to show GetFeatureInfo results in.
   */
  setWeatherLayer(key: string | null, opacity?: number, options: WeatherImageryOptions = {}): void {
    const layers = this.ctx.viewer.imageryLayers;
    if (opacity !== undefined) this.weatherOpacity = Math.max(0, Math.min(1, opacity));
    const identity =
      key && key !== 'none' ? `${key}:${options.time ?? 'latest'}:${options.revision ?? 0}` : null;
    if (identity === this.weatherIdentity && this.weather) {
      this.weather.alpha = this.weatherOpacity;
      this.ctx.viewer.scene.requestRender();
      return;
    }
    this.weatherIdentity = identity;
    if (this.weather) {
      layers.remove(this.weather, true);
      this.weather = null;
    }
    if (key === null || key === 'none') return;
    const config = WEATHER.LAYERS[key];
    if (!config) throw new Error(`Unknown weather layer "${key}"`);
    const parameters: Record<string, string> = { transparent: 'true', format: config.format || 'image/png' };
    if (options.time) parameters.time = options.time;
    if (options.revision !== undefined) parameters._refresh = String(options.revision);
    const provider = new Cesium.WebMapServiceImageryProvider({
      url: config.url,
      layers: config.layers,
      parameters,
      enablePickFeatures: false,
      credit: config.gibs
        ? 'NASA Global Imagery Browse Services (GIBS)'
        : 'Iowa Environmental Mesonet / NOAA',
    });
    let reported = false;
    provider.errorEvent.addEventListener(() => {
      if (reported) return;
      reported = true;
      const message = `Weather layer "${config.name}" could not load some tiles for ${parameters.time ?? 'the latest image'}. Try Refresh or another layer.`;
      options.onError?.(message);
      this.ctx.status('warn', message);
    });
    this.weather = layers.addImageryProvider(provider);
    this.weather.alpha = this.weatherOpacity;
    this.ctx.viewer.scene.requestRender();
  }

  /** Aurora PNG (720 x 360) over the whole globe at alpha 0.9; null removes it. */
  setAuroraImage(dataUrl: string | null): void {
    const layers = this.ctx.viewer.imageryLayers;
    if (this.aurora) {
      layers.remove(this.aurora, true);
      this.aurora = null;
    }
    if (!dataUrl) return;
    const provider = new Cesium.SingleTileImageryProvider({
      url: dataUrl,
      rectangle: Cesium.Rectangle.fromDegrees(-180, -90, 180, 90),
      tileWidth: AURORA_CANVAS_WIDTH,
      tileHeight: AURORA_CANVAS_HEIGHT,
    });
    this.aurora = layers.addImageryProvider(provider);
    this.aurora.alpha = 0.9;
  }

  destroy(): void {
    this.destroyed = true;
  }
}
